import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import useAuth from "../../hooks/useAuth";
import { HiOutlinePhotograph, HiX } from "react-icons/hi";
import { Spinner } from "./Spinner";
import { useUploadFile } from "../../hooks/cdn";
import { useTranslation } from "react-i18next";
import { PlanTier, usePermissions } from "../../utils/permissions";
import UnlockBadge from "./UnlockBadge";
import UpgradeModal from "../modals/UpgradeModal";
import { PremiumBenefit } from "./UpgradePromptContent";

interface EventImageUploadProps {
  imageUrl?: string;
  onImageUploaded: (cdnPath: string) => void;
  onImageRemoved: () => void;
  uploadLabel?: string;
  alt?: string;
  /** What the upgrade window says when the plan refuses the image; events by default. */
  upgrade?: { title: string; description: string; benefits: string[] };
}

const EventImageUpload: React.FC<EventImageUploadProps> = ({
  imageUrl,
  onImageUploaded,
  onImageRemoved,
  uploadLabel,
  alt,
  upgrade,
}) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();
  const [showImageUploadUpgradeModal, setShowImageUploadUpgradeModal] =
    useState(false);

  const handleTriggerFileInput = () => {
    if (!permissions.isAtLeastTier(PlanTier.PREMIUM)) {
      setShowImageUploadUpgradeModal(true);
      return;
    }
    inputFileRef.current?.click();
  };

  const handleUploadFile = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    if (!file.type.startsWith("image/")) {
      toast.error(t("components.eventImageUpload.errorInvalidType"));
      return;
    }

    if (file.size > 5 * 1048576) {
      toast.error(t("components.eventImageUpload.errorSize"));
      return;
    }

    uploadFile(
      {
        user_identification: customer_identification,
        entity_identification: user.entity_identification,
        data: { file: file } as any,
      },
      {
        onSuccess: (res: any) => {
          const cdnPath = res?.data?.element[0]?.cdnPath;
          if (cdnPath) {
            onImageUploaded(cdnPath);
            toast.success(t("components.eventImageUpload.successUploaded"));
          }
          if (inputFileRef.current) {
            inputFileRef.current.value = "";
          }
        },
      }
    );
  };

  const handleRemoveImage = () => {
    onImageRemoved();
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
  };

  const imageUploadBenefits: PremiumBenefit[] = upgrade
    ? upgrade.benefits.map((text, index) => ({
        id: `image-upgrade-${index}`,
        text,
      }))
    : [
        {
          id: "event-image-branding",
          text: t("dashboard.upgrade.eventImage.benefit1") as string,
        },
        {
          id: "event-image-engagement",
          text: t("dashboard.upgrade.eventImage.benefit2") as string,
        },
        {
          id: "event-image-professional",
          text: t("dashboard.upgrade.eventImage.benefit3") as string,
        },
      ];

  return (
    <div className="space-y-2">
      <input
        ref={inputFileRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleUploadFile(e.target.files)}
        style={{ display: "none" }}
      />

      {imageUrl ? (
        <div className="relative group w-full" style={{ aspectRatio: "16/9" }}>
          <img
            src={imageUrl}
            alt={alt ?? (t("components.eventImageUpload.altText") as string)}
            className="w-full h-full object-cover rounded-lg border border-gray-300"
          />
          <button
            onClick={handleRemoveImage}
            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
            type="button"
          >
            <HiX size={14} />
          </button>
        </div>
      ) : (
        <button
          onClick={handleTriggerFileInput}
          disabled={isUploading}
          type="button"
          className="w-full border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ aspectRatio: "16/9" }}
        >
          {isUploading ? (
            <>
              <Spinner size="md" />
              <span className="text-xs text-gray-600">
                {t("components.eventImageUpload.uploading")}
              </span>
            </>
          ) : (
            <>
              <HiOutlinePhotograph className="text-gray-400" size={32} />
              <span className="text-xs text-gray-600">
                {uploadLabel ?? t("components.eventImageUpload.clickToUpload")}
              </span>
              <span className="text-xs text-gray-500">
                {t("components.eventImageUpload.formatInfo")}
              </span>
              {!permissions.isAtLeastTier(PlanTier.PREMIUM) && <UnlockBadge />}
            </>
          )}
        </button>
      )}

      <UpgradeModal
        showModal={showImageUploadUpgradeModal}
        setShowModal={setShowImageUploadUpgradeModal}
        targetPlan={PlanTier.PREMIUM}
        title={
          upgrade?.title ?? (t("dashboard.upgrade.eventImage.title") as string)
        }
        description={
          upgrade?.description ??
          (t("dashboard.upgrade.eventImage.description") as string)
        }
        benefits={imageUploadBenefits}
        icon={HiOutlinePhotograph}
        iconTheme="blue"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />
    </div>
  );
};

export default EventImageUpload;
